// Канал референсных картинок промпт-бара — отдельный concern рядом с
// `useImageRoleSlots`: у кадров роли и по одному вложению на слот, у референсов
// пул до `maxReferenceImages` без ролей вовсе.
//
// Референс не кадр: он описывает, что должно быть в кадре, уезжает своим полем
// запроса и не участвует ни в ролевой раскладке, ни в счётчике «сколько картинок
// нужно режиму». Поэтому разделение пула живёт здесь, а не размазано по
// счётчикам вызывающего хука.

import { useCallback, useEffect, useMemo, useRef } from "react";
import toast from "react-hot-toast";

import { runValidationAndUpload } from "~/features/upload-attachment/runValidationAndUpload";
import { useDraftChatStore } from "~/shared/store/useDraftChatStore";

import type { TFunction } from "i18next";
import type { AttachmentCreate } from "~/entities/chat";
import type { ModeConfig } from "~/shared/data/chat-models";
import type { PendingUpload } from "~/shared/store/types";

/** Политика типов вложений в том же виде, в каком её принимает загрузчик. */
type UploadPolicy = Parameters<typeof runValidationAndUpload>[0]["policy"];

export interface UseReferenceChannelParams {
    chatId: string | null;
    currentMode: ModeConfig | null;
    /** Все вложения чата — канал выбирает из них свои. */
    items: readonly PendingUpload[];
    policy: UploadPolicy;
    upload: (files: File[], seedPatch?: Partial<PendingUpload>) => Promise<AttachmentCreate[]>;
    t: TFunction<"chat">;
}

export function useReferenceChannel({
    chatId,
    currentMode,
    items,
    policy,
    upload,
    t,
}: UseReferenceChannelParams) {
    const removePending = useDraftChatStore((s) => s.removePending);

    const maxReferences = currentMode?.maxReferenceImages ?? 0;
    const referenceItems = useMemo(() => items.filter((item) => item.channel === "reference"), [items]);
    const frameItems = useMemo(() => items.filter((item) => item.channel === undefined), [items]);

    // В конкретный слот референс не кладётся: у канала нет ролей. Пометка канала
    // ставится тем же seed-patch'ем, что и роль кадра.
    const uploadReference = useCallback(
        (files: File[]) =>
            runValidationAndUpload({
                files,
                limits: { image: maxReferences, video: 0, audio: 0 },
                currentCounts: { image: referenceItems.length, video: 0, audio: 0 },
                policy,
                upload: (accepted) => upload(accepted, { channel: "reference" }),
                t,
            }),
        [maxReferences, referenceItems.length, policy, upload, t],
    );

    // Своя очистка: канал живёт не по kind'ам, а по `maxReferenceImages`, поэтому
    // смена на режим без канала (или с меньшим лимитом) не ловится общей проверкой
    // принимаемых kind'ов — картинки остались бы набранными и уехали бы в запрос,
    // который их не принимает.
    const referenceItemsRef = useRef(referenceItems);
    referenceItemsRef.current = referenceItems;
    useEffect(() => {
        if (!chatId) return;
        const excess = referenceItemsRef.current.slice(maxReferences);
        if (excess.length === 0) return;
        for (const item of excess) removePending(chatId, item.localId);
        toast(t("prompt.attachmentsRemoved", { count: excess.length }), { icon: "ℹ️" });
    }, [maxReferences, chatId, removePending, t]);

    /** Сколько ещё референсов принимает канал — лимит пикера, открытого из колонки. */
    const referenceRoom = Math.max(maxReferences - referenceItems.length, 0);

    return { maxReferences, referenceItems, frameItems, referenceRoom, uploadReference };
}
